import React, { useMemo } from "react";
import { Box, Typography, Button, Table, Sheet, Alert } from "@mui/joy";
import useSelectedSupplier from "../../../components/WorkEstimation/components/hooks/useSelectedSupplier";
import { ShoppingCart, Target, Zap, Check } from "lucide-react";

interface InventoryItem {
    ProductName: string;
    ProductCategory?: string;
    UnitPrice: number | string;
    Quantity: number | string;
}

interface Supplier {
    SupplierName: string;
    SupplierInventory: InventoryItem[];
}

type PurchaseStrategy = "single" | "multiple";

interface SupplierComparisonProps {
    comparisonResults: Supplier[];
    purchaseStrategy?: PurchaseStrategy;
    onClose?: () => void;
    onConfirm?: () => void;
    itemSupplierSelections?: Record<string, string>;
    onItemSupplierSelect?: (productName: string, supplierName: string) => void;
}

const SupplierComparison: React.FC<SupplierComparisonProps> = ({
    comparisonResults,
    purchaseStrategy = "single",
    onClose,
    onConfirm,
    itemSupplierSelections = {},
    onItemSupplierSelect,
}) => {
    const { selectedSupplier, setSelectedSupplier } = useSelectedSupplier();

    const { productRows, totals, efficiencyMap } = useMemo(() => {
        if (!comparisonResults || comparisonResults.length === 0) {
            return { productRows: [], totals: {}, efficiencyMap: {} };
        }

        const productMap: Record<string, any> = {};
        const supplierTotals: Record<string, number> = {};
        const bestPricePerProductMap: Record<string, { supplier: string; price: number }> = {};

        comparisonResults.forEach((supplier) => {
            supplierTotals[supplier.SupplierName] = 0;
            supplier.SupplierInventory.forEach((product) => {
                if (!product.ProductName) return;
                const key = product.ProductName;

                if (!productMap[key]) {
                    productMap[key] = {
                        ProductName: product.ProductName,
                        ProductCategory: product.ProductCategory,
                        Quantity: Number(product.Quantity),
                        prices: {},
                    };
                }

                const price = Number(product.UnitPrice) || 0;
                productMap[key].prices[supplier.SupplierName] = price;

                if (price > 0) {
                    const totalPrice = price * Number(product.Quantity);
                    supplierTotals[supplier.SupplierName] += totalPrice;

                    if (!bestPricePerProductMap[key] || price < bestPricePerProductMap[key].price) {
                        bestPricePerProductMap[key] = { supplier: supplier.SupplierName, price };
                    }
                }
            });
        });

        const efficiencyMap: Record<string, string> = {};
        Object.keys(bestPricePerProductMap).forEach((key) => {
            efficiencyMap[key] = bestPricePerProductMap[key].supplier;
        });

        return { productRows: Object.values(productMap), totals: supplierTotals, efficiencyMap };
    }, [comparisonResults]);

    const bestTotalSupplier = useMemo(() => {
        if (Object.keys(totals).length === 0) return null;
        return Object.entries(totals).reduce(
            (best, [name, total]) => (!best || total < best.total ? { name, total } : best),
            null as any,
        );
    }, [totals]);

    const isSelectionComplete = useMemo(() => {
        if (purchaseStrategy === "single") {
            return !!selectedSupplier?.id;
        } else {
            return productRows.every((row) => !!itemSupplierSelections[row.ProductName]);
        }
    }, [purchaseStrategy, selectedSupplier, itemSupplierSelections, productRows]);

    if (comparisonResults.length === 0)
        return (
            <Box p={2}>
                <Alert color="neutral">No comparison data.</Alert>
            </Box>
        );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "hidden",
                bgcolor: "background.surface",
            }}
        >
            <Box
                sx={{
                    px: 2.5,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.level1",
                }}
            >
                <Box>
                    <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                        {purchaseStrategy === "single" ? "Single Source Strategy" : "Multi-Source strategy"}
                    </Typography>
                    <Typography level="body-xs" color={isSelectionComplete ? "success" : "warning"}>
                        {isSelectionComplete
                            ? "✓ Selection complete. Ready to apply."
                            : purchaseStrategy === "single"
                              ? "Please select a global supplier."
                              : `Missing selections for ${productRows.filter((r) => !itemSupplierSelections[r.ProductName]).length} row(s)`}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="plain" color="neutral" size="sm" onClick={onClose} sx={{ fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="soft"
                        color="primary"
                        size="sm"
                        disabled={!isSelectionComplete}
                        onClick={onConfirm}
                        sx={{ px: 4, fontWeight: 700 }}
                    >
                        Apply strategy
                    </Button>
                </Box>
            </Box>

            <Sheet sx={{ flex: 1, overflow: "auto" }}>
                <Table
                    stickyHeader
                    size="sm"
                    borderAxis="bothBetween"
                    stripe="odd"
                    hoverRow
                    sx={{
                        "& tr > *:first-child": {
                            position: "sticky",
                            left: 0,
                            boxShadow: "1px 0 var(--TableCell-borderColor)",
                            bgcolor: "background.surface",
                        },
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ width: 220, paddingLeft: 16 }}>
                                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                                    Line Item
                                </Typography>
                            </th>
                            {comparisonResults.map((s) => (
                                <th key={s.SupplierName} style={{ textAlign: "center", minWidth: 140 }}>
                                    <Box display="flex" flexDirection="column" gap={0.5} py={0.5}>
                                        <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                                            {s.SupplierName}
                                        </Typography>
                                    </Box>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {productRows.map((row) => (
                            <tr key={row.ProductName}>
                                <td style={{ paddingLeft: "16px" }}>
                                    <Box>
                                        <Typography level="title-sm" sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                                            {row.ProductName}
                                        </Typography>
                                        <Typography level="body-xs" sx={{ fontSize: "0.68rem" }}>
                                            {row.Quantity} · {row.ProductCategory || "Material"}
                                        </Typography>
                                    </Box>
                                </td>
                                {comparisonResults.map((s) => {
                                    const price = row.prices[s.SupplierName] || 0;
                                    const totalPrice = price * row.Quantity;
                                    const isEfficient = efficiencyMap[row.ProductName] === s.SupplierName;
                                    const isSelected =
                                        (purchaseStrategy === "multiple"
                                            ? itemSupplierSelections[row.ProductName] === s.SupplierName
                                            : selectedSupplier?.id === s.SupplierName) && price > 0;

                                    return (
                                        <td
                                            key={s.SupplierName}
                                            onClick={() =>
                                                purchaseStrategy === "multiple" && price > 0 && onItemSupplierSelect
                                                    ? onItemSupplierSelect(row.ProductName, s.SupplierName)
                                                    : null
                                            }
                                            style={{
                                                textAlign: "center",
                                                cursor:
                                                    purchaseStrategy === "multiple" && price > 0
                                                        ? "pointer"
                                                        : "default",
                                                position: "relative",
                                                opacity: price === 0 ? 0.6 : 1,
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                gap={0.5}
                                                sx={{ position: "relative" }}
                                            >
                                                <Box sx={{ width: 16, display: "flex", justifyContent: "center" }}>
                                                    {isSelected && (
                                                        <Check
                                                            size={14}
                                                            strokeWidth={3}
                                                            color="var(--standard-palette-primary-500)"
                                                        />
                                                    )}
                                                </Box>

                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "0.9rem",
                                                            fontWeight: isSelected || isEfficient ? 700 : 500,
                                                            color: isSelected
                                                                ? "primary.400"
                                                                : isEfficient
                                                                  ? "success.400"
                                                                  : price === 0
                                                                    ? "text.tertiary"
                                                                    : "text.primary",
                                                        }}
                                                    >
                                                        {price === 0 ? "—" : `$${totalPrice.toFixed(2)}`}
                                                    </Typography>
                                                    {price > 0 && (
                                                        <Typography
                                                            level="body-xs"
                                                            sx={{
                                                                mt: -0.2,
                                                                fontSize: "0.68rem",
                                                                fontWeight: isEfficient ? 700 : 500,
                                                                color: isEfficient ? "success.400" : "text.tertiary",
                                                            }}
                                                        >
                                                            ${price.toFixed(2)}/u
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Box sx={{ width: 16, display: "flex", justifyContent: "center" }}>
                                                    {isEfficient && (
                                                        <Zap
                                                            size={12}
                                                            strokeWidth={3}
                                                            fill="currentColor"
                                                            color="var(--standard-palette-success-400)"
                                                            style={{ opacity: 1, flexShrink: 0 }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot
                        style={{
                            position: "sticky",
                            bottom: 0,
                            backgroundColor: "var(--standard-palette-background-surface)",
                            zIndex: 100,
                            boxShadow: "0 -4px 12px rgba(0,0,0,0.05), 0 -1px 0 var(--standard-palette-divider)",
                        }}
                    >
                        <tr>
                            <td
                                style={{
                                    fontWeight: 800,
                                    paddingLeft: "16px",
                                    fontSize: "0.85rem",
                                    color: "var(--standard-palette-text-primary)",
                                }}
                            >
                                ESTIMATED PRICE
                            </td>
                            {comparisonResults.map((s) => (
                                <td
                                    key={s.SupplierName}
                                    style={{ textAlign: "center", paddingTop: "12px", paddingBottom: "12px" }}
                                >
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                        <Typography
                                            level="title-lg"
                                            sx={{
                                                fontWeight: 800,
                                                fontSize: "1.1rem",
                                                color:
                                                    bestTotalSupplier?.name === s.SupplierName
                                                        ? "success.400"
                                                        : "text.primary",
                                            }}
                                        >
                                            ${totals[s.SupplierName].toFixed(2)}
                                        </Typography>
                                        {(purchaseStrategy === "single" || purchaseStrategy === "multiple") && (
                                            <Button
                                                variant={
                                                    purchaseStrategy === "single"
                                                        ? selectedSupplier?.id === s.SupplierName
                                                            ? "solid"
                                                            : "outlined"
                                                        : "outlined"
                                                }
                                                size="sm"
                                                onClick={() => {
                                                    if (purchaseStrategy === "single") {
                                                        setSelectedSupplier({ id: s.SupplierName, name: s.SupplierName });
                                                    } else if (onItemSupplierSelect) {
                                                        productRows.forEach((row) => {
                                                            const hasProduct = row.prices[s.SupplierName] > 0;
                                                            if (hasProduct) {
                                                                onItemSupplierSelect(row.ProductName, s.SupplierName);
                                                            }
                                                        });
                                                    }
                                                }}
                                                sx={{ minHeight: "24px", px: 1, fontSize: "0.65rem" }}
                                            >
                                                {purchaseStrategy === "single" && selectedSupplier?.id === s.SupplierName
                                                    ? "Selected"
                                                    : "Select All"}
                                            </Button>
                                        )}
                                    </Box>
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                </Table>
            </Sheet>
        </Box>
    );
};

export { SupplierComparison };
